<template>
	<transition name="fade">
		<div
			v-if="active
				&& (hasSubtitleSlot || subtitle || title)"
			ref="toast"
			:class="[
				`toast toast--${state} toast--${position}`,
				{
					'toast--animating' : animatingOpen,
				}
			]"
			data-cy="toast"
			:data-uid="_uid"
		>
			<div
				v-if="closeable"
				@keyup.space="$emit('close')"
				@keyup.enter="$emit('close')"
			>
				<helio-icon
					class="toast__close"
					token="times"
					weight="light"
					tabindex="0"
					:aria-label="`${$t('aria-label.close-button')}: ${title}`"
					@click="$emit('close')"
				/>
			</div>

			<helio-icon
				v-if="icon"
				:token="icon"
				:weight="iconWeight"
				class="toast__icon"
			/>

			<div class="toast__content">
				<div
					v-if="title"
					class="toast__title"
				>
					{{ title }}
				</div>

				<div
					v-if="hasSubtitleSlot"
					class="toast__subtitle"
					data-cy="toast-subtitle"
				>
					<slot name="subtitle" />
				</div>

				<div
					v-else
					class="toast__subtitle"
					data-cy="toast-subtitle"
				>
					{{ subtitle }}
				</div>
			</div>

			<div
				v-if="countdown"
				class="toast__progress-wrapper"
			>
				<div
					ref="progress"
					class="toast__progress"
				/>
			</div>
		</div>
	</transition>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import Icon from '../icon/Icon.vue';

export default {
	name: 'Toast',

	components: {
		'helio-icon': Icon,
	},

	props: {
		active: {
			type: Boolean,
			required: true,
			description: 'Sets whether the Toast will be displayed',
		},

		closeable: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Sets whether the user can close the Toast',
		},

		countdown: {
			type: Number,
			required: false,
			default: null,
			validator: count => count > 0,
			description: 'Sets the length of time in seconds the Toast will remain open',
		},

		icon: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the icon that will appear in the Toast',
		},

		iconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the icon\'s weight',
		},

		offset: {
			type: Number,
			required: false,
			default: 20,
			description: 'Sets how far the Toast will be from the edge of the window or from adjacent Toasts',
		},

		position: {
			type: String,
			required: false,
			default: 'top',
			validator: value => [
				'bottom',
				'top',
			].indexOf(value) !== -1,
			description: 'Sets whether the Toast will appear at the top or bottom of the window',
		},

		state: {
			type: String,
			required: false,
			default: 'success',
			validator: value => [
				'alert',
				'danger',
				'info',
				'success',
			].indexOf(value) !== -1,
			description: 'Sets the state of the Toast',
		},

		subtitle: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the Toast\'s subtitle',
		},

		title: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the Toast\'s title',
		},
	},

	data() {
		return {
			animatingOpen: false,
		};
	},

	computed: {
		toastWidth() {
			// The toast width as been extracted to a computed property in
			// order to make it easier for us to unit test the component
			const { toast } = this.$refs;
			return toast ? toast.clientWidth : 0;
		},

		hasSubtitleSlot() {
			return !!this.$slots.subtitle || !!this.$scopedSlots.subtitle;
		},
	},

	watch: {
		active() {
			this.initiateToast();
		},
	},

	mounted() {
		if (this.active) {
			this.initiateToast();
		}

		if (!this.hasSubtitleSlot && !this.subtitle && !this.title) {
			console.warn('Toast needs at least a subtitle or a title');
		}
	},

	methods: {
		initiateToast() {
			this.$nextTick(() => {
				this.positionToast();
				if (this.countdown) {
					this.timer(this.countdown, this.countdown);
				}
			});

			this.animate();
		},

		timer(timeLeft, timeTotal) {
			if (this.active) {
				const { progress } = this.$refs;
				const interval = 1000;
				const width = (timeLeft * this.toastWidth) / timeTotal;

				if (progress) {
					progress.style.width = `${width}px`;
				}

				if (timeLeft >= 0) {
					setTimeout(() => {
						this.timer(timeLeft - 1, timeTotal);
					}, interval);
				} else {
					this.$emit('close');
				}
			}
		},

		positionToast() {
			const { toast } = this.$refs;
			const toasts = document.querySelectorAll(`.toast--${this.position}`);
			let verticalPosition = this.offset;

			if (!toast) {
				return;
			}
			// TODO: We now support multiple toasts, but it can glitch if two
			// toasts are being open while the first one is still appearing
			if (toast && toast.style[this.position] === '') {
				if (toasts[toasts.length - 2]) {
					const lastToast = toasts[toasts.length - 2];
					let lastTop = parseInt(lastToast.style[this.position].replace('px', ''), 10);

					if (!lastTop) {
						lastTop = this.offset;
					}

					verticalPosition = lastTop + lastToast.offsetHeight + this.offset;
				}

				toast.style[this.position] = `${verticalPosition}px`;
			}
		},

		animate() {
			this.animatingOpen = true;

			setTimeout(() => {
				this.animatingOpen = false;
			}, 500);
		},
	},
};
</script>

<style lang="scss" scoped src="./toast.scss" />
